import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { MessageCircle, MessageSquare, Mail, Settings, BarChart3, Globe, Copy, Code } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

const ClientDashboard = () => {
  const { user } = useAuth();
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalQueries: 0,
    todayQueries: 0,
    contextCount: 0
  });

  useEffect(() => {
    fetchClientData();
  }, []);

  const fetchClientData = async () => {
    try {
      const response = await axios.get('/clients/my-profile');
      setClientData(response.data.client);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const copyIntegrationCode = () => {
    if (clientData?.apiKey) {
      const code = `<script src="${window.location.origin}/widget/chatbot.js?apiKey=${clientData.apiKey}"></script>`;
      copyToClipboard(code);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!clientData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No client profile found. Please contact your administrator.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Welcome, {user?.name}
        </h1>
        <p className="text-gray-600">
          Manage your chatbot settings and view analytics
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <MessageCircle className="h-12 w-12 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Queries</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalQueries}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <BarChart3 className="h-12 w-12 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Today's Queries</p>
              <p className="text-2xl font-bold text-gray-900">{stats.todayQueries}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Globe className="h-12 w-12 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Knowledge Items</p>
              <p className="text-2xl font-bold text-gray-900">{stats.contextCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Client Info */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Chatbot Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500">Company Name</label>
            <p className="mt-1 text-sm text-gray-900">{clientData.name}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Website</label>
            <p className="mt-1 text-sm text-gray-900">{clientData.website || 'Not provided'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Status</label>
            <p className="mt-1">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                clientData.isActive 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {clientData.isActive ? 'Active' : 'Inactive'}
              </span>
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Chatbot Status</label>
            <p className="mt-1">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                clientData.chatbotConfig?.enabled 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {clientData.chatbotConfig?.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Embed Code Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center mb-4">
          <Code className="h-6 w-6 text-blue-500 mr-3" />
          <h2 className="text-lg font-semibold text-gray-900">Chatbot Integration</h2>
        </div>
        <p className="text-gray-600 mb-4">
          Add this code to your website's header section to embed the chatbot widget:
        </p>
        <div className="bg-gray-50 p-4 rounded-md border">
          <code className="text-sm text-gray-800 break-all block">
            {clientData?.apiKey 
              ? `<script src="${window.location.origin}/widget/chatbot.js?apiKey=${clientData.apiKey}"></script>`
              : 'Loading...'
            }
          </code>
        </div>
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-500">
            Add this code just before the closing &lt;/head&gt; tag in your HTML
          </div>
          <button
            onClick={copyIntegrationCode}
            disabled={!clientData?.apiKey}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy Code
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <Link
            to="/client/quick-questions"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <MessageSquare className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-900">Quick Questions</h3>
              <p className="text-sm text-gray-500">Manage chatbot quick question bubbles</p>
            </div>
          </Link>

          <Link
            to="/client/contact-submissions"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Mail className="h-8 w-8 text-orange-500" />
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-900">Contact Submissions</h3>
              <p className="text-sm text-gray-500">View and manage contact form submissions</p>
            </div>
          </Link>
          
          <Link
            to="/client/settings"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Settings className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-900">Chatbot Settings</h3>
              <p className="text-sm text-gray-500">Customize your chatbot appearance and behavior</p>
            </div>
          </Link>
          
          <Link
            to="/client/knowledge"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Globe className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-900">Knowledge Base</h3>
              <p className="text-sm text-gray-500">Manage your chatbot's knowledge and responses</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;